import { useRef, useState } from 'react';
import SERVICES from '../content/services';
import SectionHeading from './SectionHeading';
import Button from './Button';

const pad2 = n => String(n).padStart(2, '0');
const COUNT = SERVICES.length;

export default function ServiceExplorer() {
  const [cur, setCur] = useState(0);
  const tabs = useRef([]);
  const svc = SERVICES[cur];

  // WAI-ARIA tabs: arrow keys move between tabs.
  const onKeyDown = (e, k) => {
    let next = null;
    if (e.key === 'ArrowRight') next = (k + 1) % COUNT;
    else if (e.key === 'ArrowLeft') next = (k - 1 + COUNT) % COUNT;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = COUNT - 1;
    if (next === null) return;
    e.preventDefault();
    setCur(next);
    tabs.current[next]?.focus();
  };

  return (
    <section id="services" aria-labelledby="services-h" className="container-site py-sec-y">
      <SectionHeading
        id="services-h" eyebrow="Explore what we build" title="Six practices. One engineering team."
        aside="Pick a practice to see what it covers. Most projects combine two or three." className="pb-7"
      />

      <div role="tablist" aria-label="Services" className="no-scrollbar flex overflow-x-auto border-y-2 border-ink">
        {SERVICES.map((s, k) => {
          const on = k === cur;
          const Icon = s.icon;
          return (
            <button
              key={s.short} ref={el => { tabs.current[k] = el; }} type="button" role="tab" id={`svc-tab-${k}`}
              aria-selected={on} aria-controls="svc-panel" tabIndex={on ? 0 : -1}
              onClick={() => setCur(k)} onKeyDown={e => onKeyDown(e, k)}
              className={`flex min-h-[60px] flex-[1_0_auto] items-center gap-2.5 whitespace-nowrap px-[18px] text-left text-[15px] font-extrabold transition-colors ${k > 0 ? 'border-l-2 border-ink' : ''} ${on ? 'bg-ink text-bg' : 'bg-transparent text-ink hover:bg-neutral-200'}`}
            >
              <Icon size={18} aria-hidden="true" className="flex-none" />{s.short}
            </button>
          );
        })}
      </div>

      <div id="svc-panel" role="tabpanel" aria-labelledby={`svc-tab-${cur}`} className="grid border-b-2 border-ink nav:grid-cols-2">
        <div className="flex flex-col py-[clamp(28px,4vw,48px)] nav:pr-[clamp(0px,3vw,40px)]">
          <div className="text-eyebrow uppercase text-neutral-700">ISL / {svc.short}</div>
          <h3 className="balance mt-4 text-panel">{svc.title}</h3>
          <p className="pretty mt-[18px] max-w-[520px] text-body">{svc.desc}</p>
          <ul className="mt-7 border-t-2 border-ink">
            {svc.items.map(item => (
              <li key={item} className="flex items-center gap-3.5 border-b-1 border-divider py-3.5 font-semibold">
                <span aria-hidden="true" className="h-2 w-2 flex-none bg-accent" />{item}
              </li>
            ))}
          </ul>
          <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-4 pt-7">
            <Button href="#contact" variant="ink" size={52} spread>Discuss a {svc.lower} project</Button>
            <span className="text-[13px] font-semibold text-neutral-700">{svc.tags.join(' · ')}</span>
          </div>
        </div>

        <div className="relative min-h-[420px] overflow-hidden bg-ink nav:border-l-2 nav:border-ink">
          {SERVICES.map((s, k) => (
            <div key={s.short} aria-hidden="true" className="bw absolute inset-0 transition-opacity duration-500" style={{ opacity: k === cur ? 1 : 0 }}>
              <img src={s.image} alt={s.alt} loading="lazy" className="h-full w-full object-cover" />
            </div>
          ))}
          <div className="absolute bottom-[clamp(16px,2vw,28px)] left-[clamp(16px,2vw,28px)] w-[min(calc(100%-32px),340px)] border-2 border-ink bg-bg text-ink">
            <div className="flex items-center justify-between border-b-2 border-ink px-4 py-3 text-label uppercase">
              <span>Typical stack</span><span className="text-accent-700">{pad2(cur + 1)} / {pad2(COUNT)}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 px-4 py-3.5">
              {svc.tags.map(t => <span key={t} className="border-1.5 border-ink px-2.5 py-1 text-[13px] font-semibold">{t}</span>)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
